import React, { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  FiSun, 
  FiMoon, 
  FiCode, 
  FiFolder,
  FiHome, 
  FiMenu,
  FiX,
  FiSearch,
  FiPlus,
  FiTerminal,
  FiPlay,
  FiZap
} from 'react-icons/fi'
import { HiOutlineSparkles } from 'react-icons/hi'
import { RiFlashlightFill } from 'react-icons/ri'
import { FaFire } from 'react-icons/fa'
import toast from 'react-hot-toast'

const Navbar = ({ toggleTheme, isDark }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [scrolled, setScrolled] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navItems = [
    { path: '/', label: 'Home', icon: <FiHome />, color: 'from-red-500 to-pink-500' },
    { path: '/editor', label: 'Editor', icon: <FiCode />, color: 'from-primary-600 to-red-500' },
    { path: '/dashboard', label: 'Dashboard', icon: <FiFolder />, color: 'from-orange-500 to-red-500' },
    { path: '/explore', label: 'Explore', icon: <FiFolder />, color: 'from-purple-500 to-red-500' },
  ]

  const handleSearch = (e) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate('/explore', { state: { search: searchQuery } })
      setSearchQuery('')
      setMobileMenuOpen(false)
      toast.success(`Searching for: ${searchQuery}`)
    }
  }

  const handleQuickAction = () => {
    toast.success('Creating new project!', {
      icon: '🚀',
      duration: 3000,
    })
    navigate('/editor')
    setMobileMenuOpen(false)
  }

  const handleRunCode = () => {
    toast.success('Code compilation started!', {
      icon: '⚡',
      duration: 3000,
    })
    const event = new CustomEvent('run-code')
    window.dispatchEvent(event)
  }

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-white/90 dark:bg-gray-900/90 shadow-lg backdrop-blur-xl py-2' 
          : 'bg-transparent py-3'
      }`}>
        <div className="container mx-auto px-3 sm:px-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group relative">
              <motion.div
                whileHover={{ rotate: 12 }}
                className="relative"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-primary-600 via-red-500 to-pink-500 flex items-center justify-center shadow-lg group-hover:shadow-2xl transition-all duration-500">
                  <FaFire className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                </div>
                <div className="absolute -inset-1 bg-primary-500 rounded-2xl blur opacity-30 group-hover:opacity-50 transition-opacity duration-500"></div>
                <HiOutlineSparkles className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 text-yellow-400 w-3 h-3 sm:w-5 sm:h-5 animate-pulse" style={{ animationDuration: '3s' }} />
              </motion.div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary-600 via-red-500 to-pink-500 bg-clip-text text-transparent">
                  CodeFire
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
                  Red Theme IDE with Auto-suggestions
                </p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-4 xl:space-x-8">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`group relative px-3 py-2 xl:px-4 xl:py-2 rounded-xl transition-all duration-300 ${
                    location.pathname === item.path
                      ? 'text-white'
                      : 'text-gray-600 dark:text-gray-300 hover:text-white'
                  }`}
                >
                  {location.pathname === item.path && (
                    <motion.div
                      layoutId="navbar-active"
                      className={`absolute inset-0 bg-gradient-to-r ${item.color} rounded-xl -z-10`}
                    />
                  )}
                  <div className="flex items-center space-x-2 xl:space-x-3">
                    <span className="text-lg group-hover:scale-110 transition-transform">
                      {item.icon}
                    </span>
                    <span className="font-semibold text-sm xl:text-base">{item.label}</span>
                  </div>
                </Link>
              ))}
            </div>

            {/* Search Bar (Desktop) */}
            <div className="hidden lg:block flex-1 max-w-md mx-4 xl:mx-8">
              <form onSubmit={handleSearch} className="relative">
                <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search projects & files..."
                  className="w-full pl-12 pr-4 py-2.5 xl:py-3 rounded-xl bg-gray-100 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200 dark:border-gray-700 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 outline-none transition-all text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-sm xl:text-base"
                />
              </form>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Run Code Button */}
              <button
                onClick={handleRunCode}
                className="hidden md:flex items-center space-x-2 px-3 py-2 xl:px-4 xl:py-2 rounded-xl bg-gradient-to-r from-primary-500 to-red-500 text-white font-semibold hover:shadow-xl hover:shadow-primary-500/25 transition-all duration-300 shadow-lg text-sm xl:text-base"
              >
                <FiPlay className="w-4 h-4" />
                <span className="hidden xl:inline">Run Code</span>
                <span className="xl:hidden">Run</span>
              </button>

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center hover:shadow-lg transition-all duration-300 relative overflow-hidden group"
                aria-label="Toggle theme"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-primary-400 to-red-400 opacity-0 group-hover:opacity-20 transition-opacity"></div>
                {isDark ? (
                  <FiSun className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400" />
                ) : (
                  <FiMoon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
                )}
              </button>

              {/* Quick Action Button */}
              <button
                onClick={handleQuickAction}
                className="hidden md:flex items-center space-x-2 px-4 py-2.5 xl:px-6 xl:py-3 rounded-xl bg-gradient-to-r from-primary-500 to-red-500 text-white font-semibold hover:shadow-xl hover:shadow-primary-500/25 transition-all duration-300 shadow-lg text-sm xl:text-base"
              >
                <FiPlus className="w-4 h-4 xl:w-5 xl:h-5" />
                <span className="hidden xl:inline">New Project</span>
                <span className="xl:hidden">New</span>
              </button>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center hover:shadow-lg transition-all duration-300"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileMenuOpen ? (
                  <FiX className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700 dark:text-gray-300" />
                ) : (
                  <FiMenu className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700 dark:text-gray-300" />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-x-0 top-16 sm:top-20 z-40 lg:hidden"
          >
            <div className="container mx-auto px-3 sm:px-4">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
                {/* Mobile Search */}
                <form onSubmit={handleSearch} className="mb-4 sm:mb-6">
                  <div className="relative">
                    <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search projects & files..."
                      className="w-full pl-12 pr-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 outline-none transition-all text-gray-900 dark:text-white"
                    />
                  </div>
                </form>

                {/* Mobile Navigation */}
                <div className="space-y-2">
                  {navItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                        location.pathname === item.path
                          ? 'bg-gradient-to-r text-white'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                      } ${location.pathname === item.path ? item.color : ''}`}
                    >
                      <span className="text-xl">{item.icon}</span>
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  ))}
                </div>

                {/* Mobile Quick Action */}
                <button
                  onClick={handleQuickAction}
                  className="w-full mt-4 sm:mt-6 flex items-center justify-center space-x-2 px-6 py-3 rounded-xl bg-gradient-to-r from-primary-500 to-red-500 text-white font-semibold hover:shadow-xl hover:shadow-primary-500/25 transition-all duration-300 shadow-lg"
                >
                  <FiPlus className="w-5 h-5" />
                  <span>Create New Project</span>
                </button>

                {/* Mobile Run Button */}
                <button
                  onClick={handleRunCode}
                  className="w-full mt-3 flex items-center justify-center space-x-2 px-6 py-3 rounded-xl bg-gradient-to-r from-primary-500 to-red-500 text-white font-semibold hover:shadow-xl hover:shadow-primary-500/25 transition-all duration-300 shadow-lg"
                >
                  <FiPlay className="w-5 h-5" />
                  <span>Run Code</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default Navbar